import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute, PublicRoute } from './components/auth/ProtectedRoute';
import { LoginPage, RegisterPage } from './components/auth/AuthPages';
import { AuthCallback } from './components/auth/AuthCallback';
import { LegalConsentModal } from './components/auth/LegalConsentModal';
import { OnboardingWizard } from './components/auth/OnboardingWizard';
import { ProfileCompletionReminder, shouldShowProfileReminder } from './components/auth/ProfileCompletionReminder';
import { ForgotPasswordPage, ResetPasswordPage } from './components/auth/PasswordReset';
import { DashboardLayout } from './components/layout/DashboardLayout';

import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import NewProjectPage from './pages/NewProjectPage';
import EditProjectPage from './pages/EditProjectPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import QuotationsPage from './pages/QuotationsPage';
import QuotationDetailPage from './pages/QuotationDetailPage';
import ProposalsPage from './pages/ProposalsPage';
import ProposalDetailPage from './pages/ProposalDetailPage';
import ProfilePage from './pages/ProfilePage';
import MyRatingsPage from './pages/MyRatingsPage';
import { ChatListPage, ChatRoomPage } from './pages/ChatPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import PriceConsultationPage from './pages/PriceConsultationPage';
import NotificationsPage from './pages/NotificationsPage';
import TransactionsPage from './pages/TransactionsPage';
import AnalyticsPage from './pages/AnalyticsPage';

import { useAuth } from './contexts/AuthContext';
import { useState } from 'react';

// Global gate: any authenticated user without valid legal_acceptance sees a
// non-dismissable modal forcing them to accept Terms + Privacy + Data Deletion.
// This covers pre-existing users AND Google OAuth users who never saw the
// register form.
const LegalConsentGate = () => {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated) return null;
  if (!user?.requires_legal_acceptance) return null;
  return <LegalConsentModal />;
};

// Post-registration onboarding: fires ONLY for users whose `onboarding_completed`
// is explicitly `false` (new signups). Existing users where the field is absent
// are left untouched so the rollout doesn't disrupt the 165 users already in Atlas.
const OnboardingGate = () => {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated) return null;
  if (user?.requires_legal_acceptance) return null; // legal first, then onboarding
  if (user?.onboarding_completed !== false) return null;
  return <OnboardingWizard />;
};

// Dismissable reminder for EXISTING users (onboarding_completed is undefined —
// they signed up before the wizard existed). Invites them to complete role +
// fiscal info so they can see quotations. Can be dismissed for the session.
const ProfileReminderGate = () => {
  const { user, isAuthenticated } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  if (!isAuthenticated) return null;
  if (user?.requires_legal_acceptance) return null;
  if (user?.onboarding_completed === false) return null;
  if (dismissed) return null;
  if (!shouldShowProfileReminder(user)) return null;
  return <ProfileCompletionReminder onDismiss={() => setDismissed(true)} />;
};

// Legacy redirect component for old notification links
const QuotationDetailRedirect = () => {
  const { quotationId } = useParams();
  return <Navigate to={`/quotations/${quotationId}`} replace />;
};

const ProposalDetailRedirect = () => {
  const { proposalId } = useParams();
  return <Navigate to={`/proposals/${proposalId}`} replace />;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Detect session_id in URL hash BEFORE routes render (prevents race conditions)
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id from Google OAuth
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        <PublicRoute>
          <LandingPage />
        </PublicRoute>
      } />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={
        <PublicRoute>
          <ResetPasswordPage />
        </PublicRoute>
      } />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected Routes */}
      <Route element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route path="/dashboard" element={<DashboardPage />} />
        
        {/* Projects (Client) */}
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/new" element={<NewProjectPage />} />
        <Route path="/projects/:projectId/edit" element={<EditProjectPage />} />
        <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
        
        {/* Quotations (Provider) */}
        <Route path="/quotations" element={<QuotationsPage />} />
        <Route path="/quotations/:quotationId" element={<QuotationDetailPage />} />
        {/* Legacy redirect for old notification links */}
        <Route path="/quotation-detail/:quotationId" element={<QuotationDetailRedirect />} />
        
        {/* Proposals (Provider) */}
        <Route path="/proposals" element={<ProposalsPage />} />
        <Route path="/proposals/:proposalId" element={<ProposalDetailPage />} />
        {/* Legacy redirect for old notification links */}
        <Route path="/proposal-detail/:proposalId" element={<ProposalDetailRedirect />} />
        
        {/* Chat */}
        <Route path="/chat" element={<ChatListPage />} />
        <Route path="/chat/:quotationId" element={<ChatRoomPage />} />
        
        {/* Profile */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/ratings" element={<MyRatingsPage />} />
        <Route path="/settings" element={<ProfilePage />} />
        
        {/* Subscriptions & Price Consultation */}
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/price-consultation" element={<PriceConsultationPage />} />
        
        {/* Notifications */}
        <Route path="/notifications" element={<NotificationsPage />} />
        
        {/* Transactions */}
        <Route path="/transactions" element={<TransactionsPage />} />
        
        {/* Analytics */}
        <Route path="/analytics" element={<AnalyticsPage />} />
        
        {/* Admin */}
        <Route path="/admin" element={<AdminDashboardPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={
        <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">404</h1>
            <p className="text-zinc-600 dark:text-zinc-400 mb-6">Página no encontrada</p>
            <a href="/" className="text-orange-600 hover:underline">Volver al inicio</a>
          </div>
        </div>
      } />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <WebSocketProvider>
        <BrowserRouter>
          <AppRouter />
          <LegalConsentGate />
          <OnboardingGate />
          <ProfileReminderGate />
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </WebSocketProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
